import React, {
  createContext,
  useContext,
  FC,
  ReactNode,
  useState,
} from 'react';
import { Toast } from '@/components/molecules';
import APP_CONFIG from '@/config/app';

interface ToastContextProps {
  addToast: (content: { title: string; description: string }) => void;
}

const ToastContext = createContext<ToastContextProps | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context.addToast;
};

export const ToastProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [toast, setToast] = useState<{
    id: number;
    title: string;
    description: string;
  } | null>(null);
  const [isToastOpen, setIsToastOpen] = useState(false);

  const addToast = (content: { title: string; description: string }) => {
    if (isToastOpen) {
      setIsToastOpen(false);
      setTimeout(() => {
        const id = Date.now();
        setToast({ id, ...content });
        setIsToastOpen(true);
      }, APP_CONFIG.ANIMATION_SETTINGS.COMMON.duration);
    } else {
      const id = Date.now();
      setToast({ id, ...content });
      setIsToastOpen(true);
    }
  };

  const removeToast = () => {
    setIsToastOpen(false);
    setTimeout(() => {
      setToast(null);
    }, APP_CONFIG.ANIMATION_SETTINGS.COMMON.duration);
  };

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      {toast && (
        <Toast
          key={toast.id}
          title={toast.title}
          description={toast.description}
          isOpen={isToastOpen}
          onClose={removeToast}
        />
      )}
    </ToastContext.Provider>
  );
};
